"use client";

import { cn } from "@/lib/utils";
import { formatHKD, planDiscount, type Plan } from "./pricing";

export function PlanCard({
  plan,
  selected,
  onSelect,
}: {
  plan: Plan;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  // Derived where a list price exists: the design labels both secretary cards
  // "HK$301 OFF", but Standard is 999 -> 699, i.e. 300. A badge that disagreed
  // with the discount line in the summary would be a pricing bug, so both come
  // from one number. Plans priced at a single figure (audit & tax) fall back to
  // their literal badgeLabel, which affects nothing but the label.
  const discount = planDiscount(plan);
  const badge = plan.listPrice
    ? discount > 0
      ? `${formatHKD(discount)} OFF`
      : null
    : (plan.badgeLabel ?? null);

  return (
    <button
      type="button"
      onClick={() => onSelect(plan.id)}
      aria-pressed={selected}
      className={cn(
        "relative flex min-h-[188px] flex-1 flex-col items-start gap-4 overflow-hidden rounded-[28px] bg-[#0F1319] p-6 text-left outline outline-2 -outline-offset-2 transition-colors sm:rounded-[32px] sm:p-8",
        selected ? "outline-[#58A6FF]" : "outline-transparent hover:outline-white/20",
      )}
    >
      {/* Ambient glow. In the design this is a 171px #70A6FF circle at
          blur(71.73) sitting inside the clipped card, which reads as a wash
          coming from the lower left. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-20 -left-10 h-[171px] w-[171px] rounded-full bg-[#70A6FF] blur-[72px]"
      />

      {badge && (
        <span className="absolute right-4 top-4 flex h-8 items-center rounded-lg bg-[#28B446] px-3 font-inter text-sm font-medium text-white sm:right-5 sm:px-4 sm:text-base">
          {badge}
        </span>
      )}

      {/* pr on the title so it clears the absolutely-positioned badge above it
          on narrow cards. */}
      <div className="relative flex w-full flex-col items-start gap-1 pr-24 sm:pr-28">
        <span className="font-inter text-xl font-bold text-white sm:text-[22px]">{plan.name}</span>
        <span className="font-inter text-sm leading-[19.6px] text-[#9C9C9C]">{plan.description}</span>
      </div>

      <div className="relative">
        {plan.listPrice ? (
          <>
            <span className="font-inter text-xl font-light text-white/50 line-through sm:text-2xl">
              {formatHKD(plan.listPrice)}
            </span>{" "}
          </>
        ) : null}
        <span className="font-inter text-2xl font-extrabold text-white sm:text-[27.53px]">
          {formatHKD(plan.price)}
        </span>
        <span className="font-inter text-base font-normal text-white">{plan.period}</span>
      </div>
    </button>
  );
}
